/* eslint-disable @typescript-eslint/no-explicit-any */
import type { ISurveyQuestion } from "@/model/survey";
import {
  QuestionLabel,
  QuestionTypeCheckbox,
  QuestionTypeLinearScale,
  QuestionTypeParagraph,
  QuestionTypeRadio,
} from "./survey-question";
import { SURVEY_QUESTION_TYPE } from "@/constants";

const RenderSurveyFormLayout = ({
  question,
  questionNo,
  form,
}: {
  question: ISurveyQuestion;
  questionNo: number;
  form: any;
}) => {
  const isRequired = question.required; // Check if the question is required

  // eslint-disable-next-line react/no-unstable-nested-components
  const RenderFieldContent = (
    field: any,
    InputComponent:
      | typeof QuestionTypeParagraph
      | typeof QuestionTypeCheckbox
      | typeof QuestionTypeRadio
      | typeof QuestionTypeLinearScale,
    question: ISurveyQuestion,
  ) => (
    <div className="space-y-3">
      <QuestionLabel
        questionNo={questionNo}
        questionText={question.question}
        isRequired={isRequired}
        hasError={field.state.meta.errors.length > 0}
      />
      <div className="ml-5">
        <InputComponent
          options={question.options}
          value={field.state.value}
          onChange={field.handleChange}
        />
        {field.state.meta.errors && (
          <span className="mt-1 block text-sm text-red-500">
            {field.state.meta.errors}
          </span>
        )}
      </div>
    </div>
  );

  switch (question?.type) {
    case SURVEY_QUESTION_TYPE.PARAGRAPH:
      return (
        <form.Field
          name={`question_${question.id}`}
          validators={{
            onChange: ({ value }: { value: string | undefined }) => {
              if (isRequired && (!value || value.trim() === "")) {
                return "This field is required";
              }
              return undefined;
            },
          }}
        >
          {(field: any) =>
            RenderFieldContent(field, QuestionTypeParagraph, question)
          }
        </form.Field>
      );

    case SURVEY_QUESTION_TYPE.CHECKBOX:
      return (
        <form.Field
          id={`question_${question.id}`}
          name={`question_${question.id}`}
          defaultValue={[]}
          validators={{
            onChange: ({ value }: { value: string[] }) => {
              if (isRequired && (!value || value?.length === 0)) {
                return "Please select at least one option";
              }
              return undefined;
            },
          }}
        >
          {(field: any) =>
            RenderFieldContent(field, QuestionTypeCheckbox, question)
          }
        </form.Field>
      );

    case SURVEY_QUESTION_TYPE.RADIO:
      return (
        <form.Field
          id={`question_${question.id}`}
          name={`question_${question.id}`}
          validators={{
            onChange: ({ value }: { value: string }) => {
              if (isRequired && (!value || value?.trim() === "")) {
                return "Please select an option";
              }
              return undefined;
            },
          }}
        >
          {(field: any) =>
            RenderFieldContent(field, QuestionTypeRadio, question)
          }
        </form.Field>
      );

    case SURVEY_QUESTION_TYPE.LINEAR_SCALE:
      return (
        <form.Field
          id={`question_${question.id}`}
          name={`question_${question.id}`}
          validators={{
            onChange: ({ value }: { value: string }) => {
              if (isRequired && (!value || value?.trim() === "")) {
                return "Please select a value";
              }
              return undefined;
            },
          }}
        >
          {(field: any) =>
            RenderFieldContent(field, QuestionTypeLinearScale, question)
          }
        </form.Field>
      );
    default:
      return null;
  }
};

export default RenderSurveyFormLayout;
